import {
  BackupPolicyType,
  ResourceType,
  VaultStorageTier,
} from '@repo/api-gw-sdk';
import { useCallback, useMemo } from 'react';

import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useSupportedResourceTypes } from '@/data/inventory/useSupportedResourceTypes';
import { COLD_TIER_SUPPORTED_RESOURCE_TYPES } from '@/utils/vaultStorageTier';

export type GetBackupPolicyAvailableTypes = (
  policyType: BackupPolicyType,
  vaultStorageTier: VaultStorageTier
) => ResourceType[];

export function useBackupPolicyAvailableTypes(): GetBackupPolicyAvailableTypes {
  const {
    GcpVmHighFrequencyBackupEnabled,
    RdsHighFrequencyBackupEnabled,
    SapHanaPitrSupport,
    AzureSqlVirtualMachineSupport,
    GcpSqlVirtualMachineSupport,
    EnableBigQueryNativePolicy,
    MongoAtlasSupport,
    AwsRdsNativeBackupSupport,
    AwsEksClusterBackupSupport,
    GoogleWorkspaceSupport,
    Microsoft365Support,
    EntraIdSupport,
    AzureNativeVmBackupSupport,
    AwsEc2InAccountBackupSupport,
  } = useFeatureFlags();
  const allTypes = useSupportedResourceTypes();
  const policySupportedResourceTypes = useMemo(() => {
    const HighFrequencySupportedResourceTypes: ResourceType[] = [
      ResourceType.AwsEc2,
      ...(GcpVmHighFrequencyBackupEnabled
        ? [ResourceType.GcpComputeEngineInstance]
        : []),
      ...(RdsHighFrequencyBackupEnabled ? [ResourceType.AwsRds] : []),
      ResourceType.AwsDynamoDb,
      ResourceType.AwsKeyspacesTable,
      ResourceType.AwsS3,
      ResourceType.GcpCloudStorageBucket,
      ...(MongoAtlasSupport ? [ResourceType.AtlasMongodbCluster] : []),
    ];

    const awsNativeHighFrequencySupportedResourceTypes: ResourceType[] = [
      ...(AwsEc2InAccountBackupSupport ? [ResourceType.AwsEc2] : []),
    ];

    const pitrPolicySupportedResourceTypes: ResourceType[] = [
      ...(AzureSqlVirtualMachineSupport
        ? [ResourceType.AzureSqlVirtualMachine]
        : []),
      ...(GcpSqlVirtualMachineSupport ? [ResourceType.GcpSqlVm] : []),
      ...(SapHanaPitrSupport
        ? [ResourceType.AzureSapHanaVm, ResourceType.GcpSapHanaVm]
        : []),
    ];

    const nativePitrPolicySupportedResourceTypes: ResourceType[] = [
      ResourceType.AwsRds,
    ];

    const bigQueryNativePitrSupportedResourceTypes: ResourceType[] = [
      ...(EnableBigQueryNativePolicy ? [ResourceType.GcpBigQuery] : []),
    ];

    const azureSqlDatabaseNativePitrSupportedResourceTypes: ResourceType[] = [
      ResourceType.AzureSqlDatabase,
    ];

    const awsNativeStandardPolicySupportedResourceTypes: ResourceType[] = [
      ResourceType.AwsFsx,
      ResourceType.AwsEfs,
      ResourceType.AwsDocumentdb,
      ResourceType.AwsNeptune,
      ...(AwsRdsNativeBackupSupport ? [ResourceType.AwsRds] : []),
      ...(AwsEksClusterBackupSupport ? [ResourceType.AwsEksCluster] : []),
    ];

    const azureFilesPolicySupportedResourceTypes: ResourceType[] = [
      ResourceType.AzureFileShare,
      ...(AzureNativeVmBackupSupport ? [ResourceType.AzureVirtualMachine] : []),
    ];

    const googleWorkspacePolicySupportedResourceTypes: ResourceType[] = [
      ...(GoogleWorkspaceSupport ? [ResourceType.GoogleWorkspaceResource] : []),
    ];

    const microsoft365PolicySupportedResourceTypes: ResourceType[] = [
      ...(Microsoft365Support ? [ResourceType.Microsoft365Resource] : []),
    ];

    const entraIdPolicySupportedResourceTypes: ResourceType[] = [
      ...(EntraIdSupport ? [ResourceType.AzureEntraIdResource] : []),
    ];

    // RDS is supported by both AWS_NATIVE_STANDARD and STANDARD policies, and an Azure VM by both
    // AZURE_FILES and STANDARD: which route a given VM takes depends on its disks, not on the type,
    // so both pickers must offer it. Exclude them from the subtraction so they stay in both.
    const dualSupportedResourceTypes: ResourceType[] = [
      ResourceType.AwsRds,
      ResourceType.AzureVirtualMachine,
    ];

    const standardPolicySupportedResourceTypes: ResourceType[] = [
      ...allTypes.filter(
        (type) =>
          (!awsNativeStandardPolicySupportedResourceTypes.includes(type) ||
            dualSupportedResourceTypes.includes(type)) &&
          (!azureFilesPolicySupportedResourceTypes.includes(type) ||
            dualSupportedResourceTypes.includes(type)) &&
          type !== ResourceType.GoogleWorkspaceResource &&
          type !== ResourceType.Microsoft365Resource &&
          type !== ResourceType.AzureEntraIdResource
      ),
    ];

    const supportedResourceTypes: Record<BackupPolicyType, ResourceType[]> = {
      [BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY]:
        HighFrequencySupportedResourceTypes,
      [BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_HIGH_FREQUENCY]:
        awsNativeHighFrequencySupportedResourceTypes,
      [BackupPolicyType.BACKUP_POLICY_TYPE_PITR]:
        pitrPolicySupportedResourceTypes,
      [BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR]:
        nativePitrPolicySupportedResourceTypes,
      [BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD]:
        standardPolicySupportedResourceTypes,
      [BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_STANDARD]:
        awsNativeStandardPolicySupportedResourceTypes,
      [BackupPolicyType.BACKUP_POLICY_TYPE_BIGQUERY_NATIVE_PITR]:
        bigQueryNativePitrSupportedResourceTypes,
      [BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_SQL_DATABASE_NATIVE_PITR]:
        azureSqlDatabaseNativePitrSupportedResourceTypes,
      [BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_FILES]:
        azureFilesPolicySupportedResourceTypes,
      [BackupPolicyType.BACKUP_POLICY_TYPE_GOOGLE_WORKSPACE]:
        googleWorkspacePolicySupportedResourceTypes,
      [BackupPolicyType.BACKUP_POLICY_TYPE_MICROSOFT_365]:
        microsoft365PolicySupportedResourceTypes,
      [BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_ENTRA_ID]:
        entraIdPolicySupportedResourceTypes,
      [BackupPolicyType.BACKUP_POLICY_TYPE_UNSPECIFIED]: [],
    };

    return supportedResourceTypes;
  }, [
    allTypes,
    GcpVmHighFrequencyBackupEnabled,
    RdsHighFrequencyBackupEnabled,
    AzureSqlVirtualMachineSupport,
    GcpSqlVirtualMachineSupport,
    SapHanaPitrSupport,
    EnableBigQueryNativePolicy,
    MongoAtlasSupport,
    AwsRdsNativeBackupSupport,
    AwsEksClusterBackupSupport,
    GoogleWorkspaceSupport,
    Microsoft365Support,
    EntraIdSupport,
    AzureNativeVmBackupSupport,
    AwsEc2InAccountBackupSupport,
  ]);

  return useCallback(
    (policyType, vaultStorageTier) => {
      const availableTypes = policySupportedResourceTypes[policyType];
      return vaultStorageTier === VaultStorageTier.VAULT_STORAGE_TIER_COLD
        ? availableTypes.filter((resourceType) =>
            COLD_TIER_SUPPORTED_RESOURCE_TYPES.includes(resourceType)
          )
        : availableTypes;
    },
    [policySupportedResourceTypes]
  );
}
